import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAbortableLoad, isCanceled } from '../hooks/useAbortableLoad';
import { useThemeColors, getPrimaryButtonStyles } from '../hooks/useThemeColors';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  ArrowLeft, Plus, Search, Edit, Trash2, Loader2, Building2, AlertCircle, RotateCcw,
} from 'lucide-react';

const AreasFuncionalesPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const themeColors = useThemeColors();

  const [loading, setLoading] = useState(true);
  const [areas, setAreas] = useState([]);
  const [search, setSearch] = useState('');
  const [mostrarInactivas, setMostrarInactivas] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({ nombre: '', gerencia: '', descripcion: '', puede_responder_cliente: false });

  const fetchAreas = async (signal) => {
    setLoading(true);
    try {
      const res = await api.get('/areas-funcionales', { signal });
      setAreas(res.data || []);
    } catch (err) {
      if (isCanceled(err)) return;
      toast.error(err?.response?.data?.detail || 'No se pudieron cargar las áreas funcionales');
      setAreas([]);
    } finally {
      setLoading(false);
    }
  };

  useAbortableLoad((signal) => fetchAreas(signal), []);

  const openCreate = () => {
    setEditing(null);
    setFormData({ nombre: '', gerencia: '', descripcion: '', puede_responder_cliente: false });
    setError('');
    setDialogOpen(true);
  };

  const openEdit = (a) => {
    setEditing(a);
    setFormData({ nombre: a.nombre || '', gerencia: a.gerencia || '', descripcion: a.descripcion || '', puede_responder_cliente: a.puede_responder_cliente === true });
    setError('');
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/areas-funcionales/${editing.id}`, formData, { timeout: 30000 });
        toast.success('Área funcional actualizada');
      } else {
        await api.post('/areas-funcionales', formData, { timeout: 30000 });
        toast.success('Área funcional creada');
      }
      setDialogOpen(false);
      fetchAreas();
    } catch (err) {
      if (err.code === 'ECONNABORTED' || /timeout/i.test(err.message || '')) {
        setError('La solicitud tardó demasiado. Verifica tu conexión y reintenta.');
      } else {
        setError(err?.response?.data?.detail || 'Error al guardar el área funcional');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDesactivar = async (a) => {
    if (!window.confirm(`¿Desactivar el área funcional "${a.nombre}"? Se conservará el historial.`)) return;
    try {
      await api.delete(`/areas-funcionales/${a.id}`);
      toast.success('Área funcional desactivada');
      fetchAreas();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo desactivar');
    }
  };

  const handleReactivar = async (a) => {
    try {
      await api.put(`/areas-funcionales/${a.id}`, { activo: true });
      toast.success('Área funcional reactivada');
      fetchAreas();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo reactivar');
    }
  };

  const filtered = areas.filter((a) => {
    if (!mostrarInactivas && !a.activo) return false;
    const s = search.toLowerCase();
    return (a.nombre || '').toLowerCase().includes(s) || (a.gerencia || '').toLowerCase().includes(s);
  });

  return (
    <div className="flex flex-col gap-6 animate-fadeIn" data-testid="areas-funcionales-page">
      {/* Volver */}
      <button
        onClick={() => navigate('/admin/usuarios')}
        className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 transition-colors"
        data-testid="volver-usuarios-btn"
      >
        <ArrowLeft className="w-4 h-4" /> Volver a Usuarios
      </button>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Building2 className="w-6 h-6" /> Áreas Funcionales
          </h1>
          <p className="text-slate-500 text-sm mt-1">Catálogo de áreas funcionales y su gerencia.</p>
        </div>
        <Button onClick={openCreate} data-testid="crear-area-btn" className="text-white" {...getPrimaryButtonStyles(themeColors)}>
          <Plus className="w-4 h-4 mr-2" /> Nueva Área
        </Button>
      </div>

      {/* Filtros */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            className="pl-9"
            placeholder="Buscar por nombre o gerencia…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="buscar-area-input"
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-600 select-none">
          <Switch checked={mostrarInactivas} onCheckedChange={setMostrarInactivas} data-testid="toggle-inactivas" />
          Mostrar inactivas
        </label>
      </div>

      {loading ? (
        <Card className="border-slate-200"><CardContent className="p-0">
          <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>
        </CardContent></Card>
      ) : filtered.length === 0 ? (
        <Card className="border-slate-200"><CardContent className="p-0">
          <div className="flex flex-col items-center justify-center py-12 text-slate-500" data-testid="areas-empty">
            <Building2 className="w-12 h-12 mb-4 text-slate-300" />
            <p>No se encontraron áreas funcionales</p>
          </div>
        </CardContent></Card>
      ) : (
        <>
          {/* Móvil - cards */}
          <div className="md:hidden space-y-3">
            {filtered.map((a) => (
              <Card key={a.id} className="border-slate-200" data-testid={`area-card-${a.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div>
                      <p className="font-semibold text-slate-900">{a.nombre}</p>
                      <p className="text-sm text-slate-600">{a.gerencia}</p>
                    </div>
                    <Badge className={a.activo ? 'status-terminado' : 'bg-red-100 text-red-800'}>{a.activo ? 'Activa' : 'Inactiva'}</Badge>
                  </div>
                  {a.descripcion && <p className="text-sm text-slate-500 mb-2">{a.descripcion}</p>}
                  <p className="text-sm mb-2" data-testid={`area-frontline-mobile-${a.id}`}>
                    <span className="text-slate-500">Frontline: </span>
                    {a.puede_responder_cliente
                      ? <span className="text-emerald-600 font-medium">Sí</span>
                      : <span className="text-slate-400">-</span>}
                  </p>
                  <div className="pt-2 border-t border-slate-100 flex justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={() => openEdit(a)} data-testid={`editar-area-mobile-${a.id}`}>
                      <Edit className="w-4 h-4 mr-1" /> Editar
                    </Button>
                    {a.activo ? (
                      <Button variant="outline" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => handleDesactivar(a)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    ) : (
                      <Button variant="outline" size="sm" className="text-emerald-600 hover:text-emerald-700" onClick={() => handleReactivar(a)}>
                        <RotateCcw className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Desktop - tabla */}
          <Card className="border-slate-200 hidden md:block"><CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="table-header">
                    <TableHead>Nombre</TableHead>
                    <TableHead>Gerencia</TableHead>
                    <TableHead>Descripción</TableHead>
                    <TableHead>Frontline</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((a) => (
                    <TableRow key={a.id} className="table-row" data-testid={`area-row-${a.id}`}>
                      <TableCell className="font-medium">{a.nombre}</TableCell>
                      <TableCell>{a.gerencia}</TableCell>
                      <TableCell className="text-slate-500 text-sm max-w-[280px] truncate" title={a.descripcion || ''}>{a.descripcion || '—'}</TableCell>
                      <TableCell data-testid={`area-frontline-${a.id}`}>
                        {a.puede_responder_cliente
                          ? <span className="text-emerald-600 font-medium">Sí</span>
                          : <span className="text-slate-400">-</span>}
                      </TableCell>
                      <TableCell>
                        <Badge className={a.activo ? 'status-terminado' : 'bg-red-100 text-red-800'}>{a.activo ? 'Activa' : 'Inactiva'}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" onClick={() => openEdit(a)} data-testid={`editar-area-${a.id}`}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          {a.activo ? (
                            <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-700" onClick={() => handleDesactivar(a)} data-testid={`desactivar-area-${a.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          ) : (
                            <Button variant="ghost" size="icon" className="text-emerald-600 hover:text-emerald-700" onClick={() => handleReactivar(a)} data-testid={`reactivar-area-${a.id}`}>
                              <RotateCcw className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent></Card>
        </>
      )}

      {/* Crear/Editar */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              {editing ? 'Editar Área Funcional' : 'Nueva Área Funcional'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <div className="space-y-2">
              <Label htmlFor="nombre">Nombre *</Label>
              <Input id="nombre" data-testid="area-nombre-input" value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                placeholder="Ej: SAC, Cobranzas, Legal" required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="gerencia">Gerencia *</Label>
              <Input id="gerencia" data-testid="area-gerencia-input" value={formData.gerencia}
                onChange={(e) => setFormData({ ...formData, gerencia: e.target.value })}
                placeholder="Ej: Gerencia Inmobiliaria" required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="descripcion">Descripción</Label>
              <Textarea id="descripcion" data-testid="area-descripcion-input" value={formData.descripcion}
                onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                placeholder="Descripción opcional" rows={3} />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
              <div className="pr-3">
                <Label htmlFor="puede-responder" className="text-sm font-medium cursor-pointer">Puede responder al cliente</Label>
                <p className="text-xs text-slate-500 mt-0.5">Si está desactivado, los usuarios de esta área (excepto Administrador) no podrán responder directamente al cliente; solo por Gestión Interna.</p>
              </div>
              <Switch
                id="puede-responder"
                data-testid="area-puede-responder-switch"
                checked={formData.puede_responder_cliente}
                onCheckedChange={(checked) => setFormData({ ...formData, puede_responder_cliente: checked })}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} data-testid="guardar-area-btn" className="text-white" {...getPrimaryButtonStyles(themeColors)}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editing ? 'Actualizar' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AreasFuncionalesPage;
